import { Injectable, inject } from '@angular/core';
import { EMPTY, expand, map, Observable, scan } from 'rxjs';
import { GraphService } from 'src/app/shared/lib/api/graph.service';
import {
  CREATE_MOVIMIENTO_INTRADAY,
  GET_ALL_MOVIMIENTOS_INTRADAY,
  GET_INCOME_RECONCILIATION_INTRADAY_CANDIDATES,
  GET_MOVIMIENTO_INTRADAY,
} from '../graphql/movimientos-intraday.queries';
import { IncomeReconciliationIntradayCandidate } from '../models/movimientos-intraday.models';

export type IncomeReconciliationIntradayMode = 'TRANSFER' | 'UNPROCESSED';

export interface IntradayCandidatesProgress {
  items: IncomeReconciliationIntradayCandidate[];
  loadedPages: number;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MovimientosIntradayService {
  private readonly graphQL = inject(GraphService);

  /**
   * @description Crea un nuevo movimiento intraday
   * @param movimientoIntraday - Datos del movimiento a crear
   * @returns Observable con el resultado de la creación
   */
  create(movimientoIntraday: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: CREATE_MOVIMIENTO_INTRADAY,
      variables: { movimientoIntraday },
    });
  }

  /**
   * @description Obtiene todos los movimientos intraday
   * @returns Observable con la lista de movimientos
   */
  getAll(): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: GET_ALL_MOVIMIENTOS_INTRADAY,
      variables: {},
    });
  }

  getIncomeReconciliationCandidatesProgressively(
    mode: IncomeReconciliationIntradayMode,
    cuentaBancoId?: string | null,
    limit = 100,
  ): Observable<IntradayCandidatesProgress> {
    type CandidatesPage = {
      items: IncomeReconciliationIntradayCandidate[];
      nextToken?: string | null;
      hasMore: boolean;
    };
    type CandidatesResponse = {
      getIncomeReconciliationIntradayCandidates: CandidatesPage;
    };

    const getPage = (nextToken?: string) =>
      this.graphQL
        .queryGraphQL<CandidatesResponse>({
          query: GET_INCOME_RECONCILIATION_INTRADAY_CANDIDATES,
          variables: {
            mode,
            cuentaBancoId: cuentaBancoId || undefined,
            limit,
            nextToken,
          },
        })
        .pipe(
          map(
            (response) =>
              response.getIncomeReconciliationIntradayCandidates,
          ),
        );

    return getPage().pipe(
      expand((page) =>
        page.hasMore && page.nextToken ? getPage(page.nextToken) : EMPTY,
      ),
      scan(
        (progress: IntradayCandidatesProgress, page: CandidatesPage) => {
          const itemsById = new Map(
            progress.items.map((item) => [item.id, item]),
          );
          (page.items ?? []).forEach((item) =>
            itemsById.set(item.id, item),
          );
          return {
            items: Array.from(itemsById.values()),
            loadedPages: progress.loadedPages + 1,
            hasMore: !!page.hasMore,
          };
        },
        { items: [], loadedPages: 0, hasMore: true },
      ),
    );
  }

  /**
   * @description Obtiene un movimiento intraday específico por ID
   * @param id - ID del movimiento
   * @returns Observable con el movimiento intraday
   */
  getById(id: string): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: GET_MOVIMIENTO_INTRADAY,
      variables: { id },
    });
  }
}
